import { useMemo } from 'react'
import {
  Box,
  Button,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Typography,
} from '@mui/material'
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import CloseIcon from '@mui/icons-material/Close'
import {
  addMonths,
  eachDayOfInterval,
  endOfMonth,
  format,
  getDay,
  isSameMonth,
  startOfMonth,
} from 'date-fns'
import { cs } from 'date-fns/locale'
import type { DovolenaRequestDay } from '../types'

const WEEKDAYS = ['Po', 'Út', 'St', 'Čt', 'Pá', 'So', 'Ne']
const HOUR_CHOICES = [8, 7, 6, 5, 4, 3, 2, 1] as const
const START_HOUR_CHOICES = [6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16] as const

export function parseYmdLocal(ymd: string): Date {
  const [y, m, d] = ymd.split('-').map(Number)
  return new Date(y, (m || 1) - 1, d || 1)
}

export function toYmdLocal(d: Date): string {
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${y}-${m}-${day}`
}

export function isWeekendLocal(d: Date): boolean {
  const n = d.getDay()
  return n === 0 || n === 6
}

export function formatStartHour(hour: number | null | undefined): string {
  if (hour == null || Number.isNaN(hour)) return ''
  return `${String(hour).padStart(2, '0')}:00`
}

export function formatDovolenaDayHours(d: Pick<DovolenaRequestDay, 'hours' | 'start_hour'>): string {
  if (d.hours < 8 && d.start_hour != null) {
    return `${d.hours} h od ${formatStartHour(d.start_hour)}`
  }
  return `${d.hours} h`
}

function mondayIndex(d: Date): number {
  return (getDay(d) + 6) % 7
}

interface DovolenaDaysCalendarProps {
  month: Date
  onMonthChange: (d: Date) => void
  days: DovolenaRequestDay[]
  onChange: (days: DovolenaRequestDay[]) => void
}

export default function DovolenaDaysCalendar({
  month,
  onMonthChange,
  days,
  onChange,
}: DovolenaDaysCalendarProps) {
  const byDate = useMemo(() => {
    const map: Record<string, DovolenaRequestDay> = {}
    for (const day of days) {
      map[day.day_date] = day
    }
    return map
  }, [days])

  const cells = useMemo(() => {
    const start = startOfMonth(month)
    const end = endOfMonth(month)
    const monthDays = eachDayOfInterval({ start, end })
    const pad = mondayIndex(start)
    const blanks = Array.from({ length: pad }, () => null as Date | null)
    return [...blanks, ...monthDays]
  }, [month])

  const sortedDays = useMemo(
    () => [...days].sort((a, b) => a.day_date.localeCompare(b.day_date)),
    [days],
  )

  const totalHours = sortedDays.reduce((s, d) => s + d.hours, 0)

  const upsert = (ymd: string, hours: number, startHour?: number | null) => {
    if (isWeekendLocal(parseYmdLocal(ymd))) return
    const existing = days.find((d) => d.day_date === ymd)
    let start: number | null
    if (hours >= 8) {
      start = null
    } else if (startHour !== undefined) {
      start = startHour
    } else {
      start = existing?.start_hour ?? null
    }
    const next = days.filter((d) => d.day_date !== ymd)
    next.push({ day_date: ymd, hours, start_hour: start })
    next.sort((a, b) => a.day_date.localeCompare(b.day_date))
    onChange(next)
  }

  const remove = (ymd: string) => {
    onChange(days.filter((d) => d.day_date !== ymd))
  }

  const toggleDay = (d: Date) => {
    if (isWeekendLocal(d)) return
    const ymd = toYmdLocal(d)
    if (byDate[ymd] != null) {
      remove(ymd)
      return
    }
    if (days.length >= 60) {
      alert('V jedné žádosti lze vybrat nejvýše 60 dní.')
      return
    }
    upsert(ymd, 8, null)
  }

  return (
    <Box>
      <Box sx={{ maxWidth: 268 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
          <IconButton size="small" onClick={() => onMonthChange(addMonths(month, -1))} aria-label="Předchozí měsíc">
            <ChevronLeftIcon fontSize="small" />
          </IconButton>
          <Typography variant="body2" sx={{ fontWeight: 700, textTransform: 'capitalize' }}>
            {format(month, 'LLLL yyyy', { locale: cs })}
          </Typography>
          <IconButton size="small" onClick={() => onMonthChange(addMonths(month, 1))} aria-label="Další měsíc">
            <ChevronRightIcon fontSize="small" />
          </IconButton>
        </Box>

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(7, 1fr)',
            gap: 0.25,
          }}
        >
          {WEEKDAYS.map((w) => (
            <Typography
              key={w}
              variant="caption"
              align="center"
              sx={{
                fontWeight: 700,
                fontSize: 10,
                lineHeight: 1.2,
                color: w === 'So' || w === 'Ne' ? 'text.disabled' : 'text.secondary',
              }}
            >
              {w}
            </Typography>
          ))}
          {cells.map((d, i) => {
            if (!d) {
              return <Box key={`b-${i}`} sx={{ height: 26 }} />
            }
            const ymd = toYmdLocal(d)
            const weekend = isWeekendLocal(d)
            const selected = byDate[ymd] != null
            const outside = !isSameMonth(d, month)
            return (
              <Box
                key={ymd}
                component="button"
                type="button"
                disabled={weekend}
                onClick={() => toggleDay(d)}
                title={
                  weekend
                    ? 'Víkendy nelze vybrat'
                    : selected
                      ? `${format(d, 'd. M. yyyy')} — ${formatDovolenaDayHours(byDate[ymd])} (kliknutím odeberete)`
                      : `${format(d, 'd. M. yyyy')} — přidat (8 h)`
                }
                sx={{
                  height: 26,
                  border: selected ? '2px solid' : '1px solid',
                  borderColor: selected ? 'primary.main' : 'divider',
                  borderRadius: 0.5,
                  bgcolor: selected ? 'primary.main' : weekend ? 'action.disabledBackground' : 'background.paper',
                  color: weekend ? 'text.disabled' : selected ? 'primary.contrastText' : 'text.primary',
                  cursor: weekend ? 'not-allowed' : 'pointer',
                  pointerEvents: weekend ? 'none' : 'auto',
                  opacity: weekend ? 0.55 : outside ? 0.45 : 1,
                  p: 0,
                  fontSize: 11,
                  fontWeight: 600,
                  lineHeight: 1,
                }}
              >
                {d.getDate()}
              </Box>
            )
          })}
        </Box>
      </Box>

      <Typography variant="body2" sx={{ mt: 1.25, mb: 0.5, fontWeight: 600 }}>
        Celkem: {totalHours} h · {sortedDays.length} {sortedDays.length === 1 ? 'den' : 'dní'}
        {sortedDays.length > 0 && (
          <>
            {' '}
            · {format(parseYmdLocal(sortedDays[0].day_date), 'd. M. yyyy')} –{' '}
            {format(parseYmdLocal(sortedDays[sortedDays.length - 1].day_date), 'd. M. yyyy')}
          </>
        )}
      </Typography>
      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
        Kliknutím přidáte pracovní den (8 h). Při méně než 8 h uveďte, od které hodiny volno začíná.
      </Typography>

      {sortedDays.length > 0 && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
          {sortedDays.map((day) => (
            <Box
              key={day.day_date}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.75,
                flexWrap: 'wrap',
                border: '1px solid',
                borderColor: day.hours < 8 && day.start_hour == null ? 'error.main' : 'divider',
                borderRadius: 1,
                px: 0.75,
                py: 0.4,
              }}
            >
              <Typography variant="body2" sx={{ minWidth: 102, fontWeight: 500, fontSize: 13 }}>
                {format(parseYmdLocal(day.day_date), 'EEE d. M.', { locale: cs })}
              </Typography>
              <FormControl size="small" sx={{ minWidth: 78 }}>
                <InputLabel id={`h-${day.day_date}`}>Hodin</InputLabel>
                <Select
                  labelId={`h-${day.day_date}`}
                  label="Hodin"
                  value={day.hours}
                  onChange={(e) => upsert(day.day_date, Number(e.target.value))}
                >
                  {HOUR_CHOICES.map((h) => (
                    <MenuItem key={h} value={h}>
                      {h} h
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              {day.hours < 8 && (
                <FormControl size="small" required sx={{ minWidth: 108 }} error={day.start_hour == null}>
                  <InputLabel id={`s-${day.day_date}`}>Od hodiny</InputLabel>
                  <Select
                    labelId={`s-${day.day_date}`}
                    label="Od hodiny"
                    value={day.start_hour ?? ''}
                    onChange={(e) => {
                      const v = e.target.value
                      upsert(day.day_date, day.hours, v === '' ? null : Number(v))
                    }}
                  >
                    {START_HOUR_CHOICES.map((h) => (
                      <MenuItem key={h} value={h}>
                        {formatStartHour(h)}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
              <IconButton size="small" onClick={() => remove(day.day_date)} aria-label="Odebrat den">
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>
          ))}
        </Box>
      )}

      {sortedDays.length > 0 && (
        <Button size="small" sx={{ mt: 0.75 }} onClick={() => onChange([])}>
          Vymazat všechny dny
        </Button>
      )}
    </Box>
  )
}
